import { FC } from 'react';
import { Link, RadioTile, Tag } from '@carbon/react';
import {
  SuggestionTile,
  type SuggestionTileField,
} from 'components/suggestion-tile';
import type { WellSuggestion } from './types';
import { WellConfidenceBreakdownTable } from './well-confidence-breakdown-table';
import wellStyles from './wells.module.scss';

// One ranked Well-match candidate in the horizontal suggestion strip. The card
// anatomy (eyebrow + badge, title, identity fields, confidence + breakdown) is
// the shared `SuggestionTile` shell; this wrapper supplies the well-specific
// container (a fixed-width scroll-snap `RadioTile` with match-state tints), the
// well field set, and the 6-factor breakdown table.

interface Props {
  suggestion: WellSuggestion;
  index: number;
  checked: boolean;
  /** Locked once the row is confirmed/removed (no further selection). */
  disabled: boolean;
  /** True when this card is the confirmed match on a reviewed row. */
  isConfirmedTile: boolean;
  wellId: number;
  onSelect: (suggestionId: number) => void;
}

// Optional identity rows: only rendered when the candidate carries the value
// (matches the prior inline conditionals). Built once so the field list stays
// declarative.
const optionalField = (
  label: string,
  value: string | null | undefined
): SuggestionTileField[] => (value ? [{ label, value }] : []);

export const WellSuggestionCard: FC<Props> = ({
  suggestion,
  index,
  checked,
  disabled,
  isConfirmedTile,
  wellId,
  onSelect,
}) => {
  // The Recommended TAG shows whenever this is the AI pick on an unreviewed
  // row. There is no longer a Recommended background tint — green is reserved
  // exclusively for the confirmed (marked-correct) tile, so a recommended-but-
  // unconfirmed card stays grey like every other candidate.
  const showRecommendedTag = suggestion.isRecommended && !isConfirmedTile;

  const fields: SuggestionTileField[] = [
    { label: 'UWI / API Number', value: suggestion.uwi ?? '—' },
    { label: 'Hole Direction', value: suggestion.wellType ?? '—' },
    { label: 'Well Status', value: suggestion.wellStatus ?? '—' },
    { label: 'County/State', value: suggestion.countyState ?? '—' },
    { label: 'Formation', value: suggestion.formation ?? '—' },
    { label: 'Legal Description', value: suggestion.legalDescription ?? '—' },
    ...optionalField('Quarter / Subdivision', suggestion.quarterSubdivision),
    ...optionalField('SHL (Surface Hole Location)', suggestion.shl),
    ...optionalField('BHL (Bottom Hole Location)', suggestion.bhl),
    ...optionalField('FTP (First Take Point)', suggestion.ftp),
    ...optionalField('LTP (Last Take Point)', suggestion.ltp),
    ...optionalField('Abstract / Survey', suggestion.abstractSurvey),
    ...optionalField('Prospect', suggestion.prospect),
  ];

  return (
    <SuggestionTile
      eyebrow={
        <>
          {`AI Suggestion ${index + 1}`}
          {showRecommendedTag && (
            <Tag type="cyan" size="sm">
              Recommended
            </Tag>
          )}
        </>
      }
      headerStatus={
        isConfirmedTile ? (
          <Tag type="green" size="sm">
            Confirmed
          </Tag>
        ) : undefined
      }
      title={
        <Link
          href="#"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {suggestion.wellName ?? 'Unnamed well'}
        </Link>
      }
      fields={fields}
      confidenceScore={suggestion.confidenceScore}
      breakdown={
        <WellConfidenceBreakdownTable factors={suggestion.confidenceFactors} />
      }
      container={(body) => (
        <RadioTile
          id={`well-match-${wellId}-${suggestion.id}`}
          name={`well-match-${wellId}`}
          value={String(suggestion.id)}
          checked={checked}
          disabled={disabled}
          className={[
            wellStyles.matchTile,
            isConfirmedTile ? wellStyles.matchTileConfirmed : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onChange={() => onSelect(suggestion.id)}
        >
          {body}
        </RadioTile>
      )}
    />
  );
};
